import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Button } from 'primereact/button'
import {
  Column,
  ColumnBodyOptions,
  ColumnEditorOptions,
} from 'primereact/column'
import {
  DataTableDataSelectableEvent,
  DataTableEditingRows,
  DataTableRowEditCompleteEvent,
  DataTableRowEditEvent,
  DataTableRowEditSaveEvent,
  DataTableRowEditValidatorOptions,
  DataTableValue,
  DataTableValueArray,
} from 'primereact/datatable'
import {
  Dropdown,
  DropdownChangeEvent,
  DropdownProps,
} from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'

import { useMode } from '../../../../../../../hooks'
import {
  ConfigurableComponent,
  ConfigurableTable,
} from '../../../../../../../utils/wrapper'
import { CompanyCode } from '../../../data'
import { FormValues } from '../common'

type Props = {
  formik: FormikProps<FormValues>
  config: any
  getFormErrorMessage: (name: keyof FormValues) => false | JSX.Element
  getNestedFormErrorMessage: (
    index: number,
    fieldName: string,
  ) => false | JSX.Element
  availableCompanyCodesOptions?: CompanyCode[]
  onEditingModeChange?: (isEditing: boolean) => void
}

export const AssignedCompanyCodes: React.FC<Props> = ({
  formik,
  config,
  getFormErrorMessage,
  getNestedFormErrorMessage,
  availableCompanyCodesOptions,
  onEditingModeChange,
}) => {
  const { t } = useTranslation()
  const mode = useMode()

  const [editingRows, setEditingRows] = useState<DataTableEditingRows>({})
  const [selectedCompanyCodes, setSelectedCompanyCodes] =
    useState<CompanyCode[]>()

  const isEditingMode = Object.keys(editingRows).length > 0

  // Notify parent component about editing mode changes
  useEffect(() => {
    onEditingModeChange?.(isEditingMode)
  }, [isEditingMode, onEditingModeChange])

  // Check if there are available options for new rows
  const usedCompanyCodeIds = formik.values.companyCodes
    ?.map(companyCode => companyCode.Common?.DisplayId)
    .filter(id => id) // Remove empty/undefined IDs

  const hasAvailableOptions =
    availableCompanyCodesOptions?.some(
      option => !usedCompanyCodeIds?.includes(option.Common?.DisplayId),
    ) || false

  const handleAddNewRow = async () => {
    const newRowIndex = formik.values.companyCodes?.length

    // Check if there's only one available option left
    const availableOptions = availableCompanyCodesOptions?.filter(
      option => !usedCompanyCodeIds?.includes(option.Common?.DisplayId),
    )

    const shouldAutoSelect = availableOptions?.length === 1
    const selectedOption = shouldAutoSelect ? availableOptions[0] : undefined

    const newRowData: CompanyCode = selectedOption
      ? {
          // If auto-selecting, use the complete CompanyCode object
          ...selectedOption,
        }
      : {
          // If not auto-selecting, create empty row
          Id: `newCompanyCode-${newRowIndex}`,
          Common: {
            DisplayId: '',
            Company: '',
            SalesTaxID: '',
            TaxNumber: '',
          },
          IsStandard: false,
          Address: {
            Street: '',
            PostalCode: '',
            City: '',
            Country: '',
          },
          CustomFields: [],
        }

    await formik.setFieldValue(`companyCodes.${newRowIndex}`, newRowData)

    // Set editing mode with correct ID
    const editingId = selectedOption
      ? selectedOption.Id!
      : `newCompanyCode-${newRowIndex}`
    setEditingRows(prev => ({
      ...prev,
      [editingId]: true,
    }))
  }

  const handleDeleteRows = async (companyCodes: CompanyCode[]) => {
    if (companyCodes) {
      const newCompanyCodes = formik.values.companyCodes?.filter(
        companyCode =>
          !companyCodes.find(
            codeToDelete => codeToDelete.Id === companyCode.Id,
          ),
      )

      await formik.setFieldValue('companyCodes', newCompanyCodes)

      // Only remove the deleted elements from the selection
      setSelectedCompanyCodes(
        prevSelected =>
          prevSelected?.filter(
            selected =>
              !companyCodes.find(deleted => deleted.Id === selected.Id),
          ) || [],
      )
    }
  }

  const headerActions = () => {
    if (mode === 'add' || mode === 'edit') {
      // Check if we have at least one company code and disable delete if only one remains
      const hasOnlyOneCompanyCode =
        (formik.values.companyCodes?.length || 0) <= 1
      const wouldDeleteAll =
        selectedCompanyCodes?.length === formik.values.companyCodes?.length

      return (
        <>
          <Button
            label={t(
              'masterDataManagement.suppliers.table.dialog.form.assignedCompanyCodes.table.delete.label',
            )}
            onClick={() => handleDeleteRows(selectedCompanyCodes!)}
            icon="pi pi-trash"
            outlined
            disabled={
              !selectedCompanyCodes?.length ||
              isEditingMode ||
              hasOnlyOneCompanyCode ||
              wouldDeleteAll
            }
          />

          <Button
            label={t(
              'masterDataManagement.suppliers.table.dialog.form.assignedCompanyCodes.table.add.label',
            )}
            onClick={handleAddNewRow}
            icon="pi pi-plus"
            outlined
            disabled={isEditingMode || !hasAvailableOptions}
          />
        </>
      )
    }
  }

  // DisplayId editor (readonly display)
  const displayIdEditor = (options: ColumnEditorOptions) => {
    const displayId =
      formik.values.companyCodes?.[options.rowIndex]?.Common?.DisplayId

    const displayIdComponent = (
      <InputText id="displayId" className="w-full" value={displayId} disabled />
    )

    const displayIdErrorMsg = (
      <div>
        {getNestedFormErrorMessage(options.rowIndex, 'Common.DisplayId')}
      </div>
    )

    const displayIdWrapper = (
      <ConfigurableComponent
        config={config}
        component={displayIdComponent}
        errorMsg={displayIdErrorMsg}
        className="m-0"
      />
    )

    return <>{displayIdWrapper}</>
  }

  // Company editor
  const companyEditor = (options: ColumnEditorOptions) => {
    const selectedCompanyTemplate = (
      option: CompanyCode,
      props: DropdownProps,
    ) => {
      if (option) return companyOptionTemplate(option)

      return <span>{props.placeholder}</span>
    }

    const companyOptionTemplate = (option: CompanyCode) => {
      return <span>{option.Common.Company}</span>
    }

    // Filter options dynamically for this specific row
    const currentRowValue = formik.values.companyCodes?.[options.rowIndex]

    // Get IDs used by OTHER rows (excluding current row)
    const usedDisplayIds = formik.values.companyCodes
      ?.filter((_, index) => index !== options.rowIndex) // Exclude current row
      ?.map(companyCode => companyCode.Common?.DisplayId)
      .filter(id => id) // Remove empty/undefined IDs

    const filteredOptions = availableCompanyCodesOptions?.filter(
      option => !usedDisplayIds?.includes(option.Common?.DisplayId),
    )

    const handleChange = (event: DropdownChangeEvent) => {
      const selectedCompanyCode: CompanyCode = event.target.value
      const oldRowId = currentRowValue?.Id

      formik.setFieldValue(
        `companyCodes.${options.rowIndex}`,
        selectedCompanyCode,
      )

      // Update editingRows if the ID changed (from newCompanyCode-X to actual ID)
      if (
        oldRowId &&
        selectedCompanyCode.Id &&
        oldRowId !== selectedCompanyCode.Id
      ) {
        setEditingRows(prev => {
          const newEditingRows = { ...prev }
          // Remove old ID and add new ID
          delete newEditingRows[oldRowId]
          newEditingRows[selectedCompanyCode.Id!] = true
          return newEditingRows
        })
      }
    }

    const companyComponent = (
      <Dropdown
        autoFocus
        id="company"
        value={currentRowValue}
        onChange={handleChange}
        options={filteredOptions}
        optionLabel="Common.Company"
        className="w-full"
        valueTemplate={selectedCompanyTemplate}
        itemTemplate={companyOptionTemplate}
      />
    )

    const companyErrorMsg = (
      <div>{getNestedFormErrorMessage(options.rowIndex, 'Common.Company')}</div>
    )

    const companyWrapper = (
      <ConfigurableComponent
        config={config}
        component={companyComponent}
        errorMsg={companyErrorMsg}
        className="m-0"
      />
    )

    return <>{companyWrapper}</>
  }

  // Action template
  const actionBodyTemplate = (
    rowData: CompanyCode,
    options: ColumnBodyOptions,
  ) => {
    if (options.rowEditor?.editing)
      return (
        <>
          <Button
            icon="pi pi-times"
            className="p-button-rounded p-button-text"
            onClick={event => {
              if (options.rowEditor?.onCancelClick) {
                options.rowEditor.onCancelClick(event)
              }
            }}
          />

          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-text"
            onClick={event => {
              if (options.rowEditor?.onSaveClick) {
                options.rowEditor.onSaveClick(event)
              }
            }}
          />
        </>
      )

    if (mode === 'add' || mode === 'edit') {
      // Prevent deletion if only one company code remains
      const hasOnlyOneCompanyCode =
        (formik.values.companyCodes?.length || 0) <= 1

      return (
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text"
          severity="danger"
          onClick={async () => {
            await handleDeleteRows([rowData])
          }}
          disabled={isEditingMode || hasOnlyOneCompanyCode}
        />
      )
    }
  }

  const onRowEditInit = (event: DataTableRowEditEvent) => {}

  const onRowEditChange = (event: DataTableRowEditEvent) => {
    setEditingRows(event.data)
  }

  const onRowEditCancel = (event: DataTableRowEditEvent) => {
    // Remove the row when cancel is clicked
    const updatedCompanyCodes = formik.values.companyCodes?.filter(
      (_, index) => index !== event.index,
    )
    formik.setFieldValue('companyCodes', updatedCompanyCodes)
  }

  const rowEditValidator = (
    data: DataTableValue,
    options: DataTableRowEditValidatorOptions<DataTableValueArray>,
  ) => {
    // Trigger validation for this specific field with proper nested structure
    formik.setFieldTouched(
      `companyCodes.${options.rowIndex}.Common.DisplayId`,
      true,
    )
    formik.setFieldTouched(
      `companyCodes.${options.rowIndex}.Common.Company`,
      true,
    )

    // Check for validation errors
    const companyCodeErrors = formik.errors.companyCodes as any
    const rowErrors = companyCodeErrors?.[options.rowIndex]
    const hasErrors = rowErrors && Object.keys(rowErrors).length > 0
    const isValid = !hasErrors

    return isValid
  }

  const onRowEditSave = (
    event: DataTableRowEditSaveEvent<DataTableValueArray>,
  ) => {}

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    setEditingRows({})
  }

  const isDataSelectable = (
    event: DataTableDataSelectableEvent,
  ): boolean | null => {
    return !isEditingMode
  }

  const companyCodesError =
    typeof formik.errors.companyCodes === 'string' ? (
      <div>{getFormErrorMessage('companyCodes')}</div>
    ) : null

  return (
    <>
      <ConfigurableTable
        config={config.table}
        data={formik.values.companyCodes}
        headerActions={headerActions()}
        stateKey="table-supplier-company-codes"
        checkboxen={mode === 'add' || mode === 'edit'}
        isDataSelectable={isDataSelectable}
        selected={selectedCompanyCodes}
        setSelected={setSelectedCompanyCodes}
        dataKey="Id"
        editMode="row"
        editingRows={editingRows}
        // Callbacks
        onRowEditInit={onRowEditInit}
        onRowEditChange={onRowEditChange}
        onRowEditCancel={onRowEditCancel}
        rowEditValidator={rowEditValidator}
        onRowEditSave={onRowEditSave}
        onRowEditComplete={onRowEditComplete}
        className="w-full"
      >
        <Column
          key="Common.DisplayId"
          field="Common.DisplayId"
          header="DisplayId"
          editor={displayIdEditor}
          className="w-6"
        ></Column>
        <Column
          key="Common.Company"
          field="Common.Company"
          header="Company"
          editor={companyEditor}
          className="w-6"
        ></Column>
        <Column
          rowEditor
          key="buttons"
          align="right"
          body={actionBodyTemplate}
          className="flex no-wrap gap-2"
        />
      </ConfigurableTable>

      {companyCodesError}
    </>
  )
}
